import {
    AdmitRequest,
    admitVerb,
    AllDaughtersOfWasteRequest,
    allDaughtersOfWasteTqk,
    allSurchargeRevisionsTqk,
    allWasteRevisionStubsVerb,
    ArriveResponse,
    arriveVerb,
    AttachPhotoToStampRequest,
    AttachPhotoToStampResponse,
    attachPhotoToStampVerb,
    bundleForWasteTqk,
    bundleStampByIdTqk,
    CompleteBundleOffloadingRequest,
    CompleteBundleOffloadingResponse,
    completeBundleOffloadingVerb,
    CompleteBundleOnloadingRequest,
    CompleteBundleOnloadingResponse,
    completeBundleOnloadingVerb,
    crewTenuresByTruckDuringVerb,
    DisclaimScheduledTransferRequest,
    DisclaimScheduledTransferResponse,
    disclaimScheduledTransferVerb,
    DismountTruckResponse,
    dismountTruckVerb,
    KingBasura,
    ManifestType,
    MothersOfWasteResponse,
    mothersOfWasteTqk,
    MountTruckRequest,
    MountTruckResponse,
    mountTruckVerb,
    NewBundleStampFood,
    NewGroundedBundleFood,
    NewSurchargeFlavorFood,
    NewTriplegFood,
    NewWasteFood,
    PackingMode,
    PatchBundleRequest,
    PatchBundleResponse,
    patchBundleVerb,
    PatchScheduledTransferRequest,
    PatchScheduledTransferResponse,
    patchScheduledTransferVerb,
    PatchSurchargeRequest,
    PatchSurchargeResponse,
    patchSurchargeVerb,
    PatchTriplegRequest,
    PatchTriplegResponse,
    patchTriplegVerb,
    PatchWasteRequest,
    PatchWasteResponse,
    patchWasteVerb,
    RetireWasteRequest,
    RetireWasteResponse,
    retireWasteVerb,
    ScheduleBundlePickupRequest,
    ScheduleBundlePickupResponse,
    scheduleBundlePickupVerb,
    SelfResponse,
    selfTqk,
    setSystemConfigVerb,
    SpawnGroundedBundleResponse,
    spawnGroundedBundleVerb,
    SpawnWasteResponse,
    spawnWasteTqk,
    StampableClassname,
    StampAgentRequest,
    StampAgentResponse,
    stampAgentVerb,
    StampBundleResponse,
    stampBundleVerb,
    stampsForAgentVerb,
    stampsForBundleTqk,
    stampsForTruckVerb,
    StampTruckRequest,
    StampTruckResponse,
    stampTruckVerb,
    StampWasteRequest,
    StampWasteResponse,
    stampWasteVerb,
    StartBundleOffloadingRequest,
    StartBundleOffloadingResponse,
    startBundleOffloadingVerb,
    StartBundleOnloadingRequest,
    StartBundleOnloadingResponse,
    startBundleOnloadingVerb,
    StartTriplegResponse,
    startTriplegVerb,
    StoreWastePermanentRequest,
    storeWastePermanentVerb,
    Surcharge,
    SurchargeableClassname,
    surchargeByIdTqk,
    surchargeFlavorByIdTqk,
    SurchargeFlavorUpsertResult,
    surchargesForSubjectTqk,
    SystemConfig,
    transfersForTruckDuringVerb,
    TransferStub,
    Tripleg,
    triplegsForTruckDuringVerb,
    TruckOrVenueStub,
    truckTenuresByCrewDuringVerb,
    Unit,
    UpsertWasteFlavorRequest,
    upsertWasteFlavorVerb,
    useFullAllDaughtersOfWaste,
    useFullOpenTransfersForTruck,
    useFullScheduledTransfersAwaitingTruck,
    useGetVersionInfo,
    useKingBasuraMothership,
    VersionInfo,
    Waste,
    wasteByIdTqk,
    wasteFlavorByIdTqk,
    wasteFlavorRevisionCountTqk,
    WasteFlavorUpsertResult,
    wasteLocationTqk,
    wastesForBundleTqk,
    watchAgentPresenceByIdTqo,
    watchBundleByIdTqo,
    WatchBundleForWasteResponse,
    watchCrewByTruckTqo,
    watchManifestTypesTqo,
    watchPackingModesTqo,
    WatchScheduledTransfersAwaitingTruckRequest,
    watchTransferByIdTqo,
    watchUnitsTqo,
    WatchWasteLocationRequest
} from "../rpc/webrpc-client.gen.ts";
import {spawnTstzSpan, todaySpan, TstzSpan} from "../tstzspan.ts";
import {
    DefaultError,
    QueryClient,
    QueryFilters,
    queryOptions,
    skipToken,
    useMutation,
    useQueries,
    useQueryClient,
} from "@tanstack/react-query";
import {RevisionStub} from "../components/RevisionBrowser.tsx";
import {useCallback, useMemo} from "react";
import {z} from "zod/v4";
import {useBomb} from "../bomb/bomb.tsx";
import {useRefreshableInfallibleQuery, useRefreshableQuery} from "../util/tqrefresh.tsx";
import {mergeUseQueriesWithInfill} from "./mergeUseQueriesWithInfill.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {Armap, armapify, cleanse, Ided, NADA} from "../util/arutil.tsx";
import {isActive} from "./tqutil.tsx";
import {InfallibleUseQueryResult, useInfallibleQuery} from "../util/useInfallibleQuery.tsx";

export function useMothership() {
    return useKingBasuraMothership();
}

// THESE ARE IMPORTANT
// it's essential to split the duration out in the query key so that
// all spans can be invalidated at once that refer to the same agent.

export interface AgentDuringProps {
    agentId: string;
    span?: TstzSpan;
}

function agentDuringTqxk({verb, agentId, span}: AgentDuringProps & { verb: string }) {
    const rv = [verb, {agentId}];
    if (span) {
        rv.push(span.toString());
    }
    return rv;
}

export function agentStampsDuringTqxk(args: AgentDuringProps) {
    return agentDuringTqxk({...args, verb: stampsForAgentVerb});
}

export function agentStampsDuringTqxo(mama: KingBasura, args: AgentDuringProps) {
    const span = args.span ?? todaySpan();
    return queryOptions({
        queryKey: agentStampsDuringTqxk(args),
        queryFn: () => mama.stampsForAgentDuring({agentId: args.agentId, during: span.toString()}),
        select: (rpc) => rpc.stamps,
    });
}

export function useStampAgentMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const bomb = useBomb();
    const rv = useMutation<StampAgentResponse, DefaultError, StampAgentRequest>({
        mutationKey: [stampAgentVerb],
        mutationFn: (args) => mama.stampAgent(args),
        onSuccess: ({stamp}) => {
            tqc.invalidateQueries({queryKey: agentStampsDuringTqxk({agentId: stamp.subjectId})})
                .then(() => {
                    const rpcself = tqc.getQueryData<SelfResponse>(selfTqk);
                    if (rpcself?.srv.self.id === stamp.subjectId) {
                        return tqc.invalidateQueries({queryKey: selfTqk});
                    }
                }).catch(bomb);
        }
    });
    return rv;
}

export function truckCrewTenuresByAgentDuringTqxk(args: AgentDuringProps) {
    return agentDuringTqxk({...args, verb: truckTenuresByCrewDuringVerb});
}

export function truckCrewTenuresByAgentDuringTqxo(mama: KingBasura, args: AgentDuringProps) {
    const span = args.span ?? todaySpan();
    return queryOptions({
        queryKey: truckCrewTenuresByAgentDuringTqxk(args),
        queryFn: () => mama.truckTenuresByCrewDuring({crewId: args.agentId, during: span.toString()}),
    });
}

export interface TruckDuringProps {
    truckId: string;
    span?: TstzSpan;
}

function truckDuringTqxk({verb, truckId, span}: TruckDuringProps & { verb: string }) {
    const rv = [verb, {truckId}];
    if (span) {
        rv.push(span.toString());
    }
    return rv;
}

export function truckStampsDuringTqxk(args: TruckDuringProps) {
    return truckDuringTqxk({...args, verb: stampsForTruckVerb});
}

export function truckStampsDuringTqxo(mama: KingBasura, args: TruckDuringProps) {
    const span = args.span ?? todaySpan();
    return queryOptions({
        queryKey: truckStampsDuringTqxk(args),
        queryFn: () => mama.stampsForTruckDuring({
            truckId: args.truckId, during: span.toString()
        })
    });
}

export function useStampTruckMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const rv = useMutation<StampTruckResponse, DefaultError, StampTruckRequest>({
        mutationKey: [stampTruckVerb],
        mutationFn: (args) => mama.stampTruck(args),
        onSuccess: ({stamp}) => tqc.invalidateQueries(
            {queryKey: truckStampsDuringTqxk({truckId: stamp.subjectId})}),
    });
    return rv;
}

export function crewTenuresByTruckTqxk(args: TruckDuringProps) {
    return truckDuringTqxk({...args, verb: crewTenuresByTruckDuringVerb});
}

export function useActiveCrewPresencesForTruck(args?: { truckId: string }) {
    const mama = useMothership();
    const spineTqrv = useRefreshableQuery(watchCrewByTruckTqo(mama, args));
    const tqos = (spineTqrv.data ?? []).map(tenure =>
        watchAgentPresenceByIdTqo(mama, {agentId: tenure.crew.id}));

    const rv = useQueries(spineTqrv.isSuccess
        ? {
            queries: tqos,
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});
    return rv;
}

export function triplegsForTruckDuringTqxk(args: TruckDuringProps) {
    return truckDuringTqxk({...args, verb: triplegsForTruckDuringVerb});
}

export function transfersForTruckDuringTqxk(args: TruckDuringProps) {
    return truckDuringTqxk({...args, verb: transfersForTruckDuringVerb});
}

// export const BundledTransfer = z.object({
//     ...Transfer.shape,
//     bundle: Bundle,
// });
//
// export function useBundledFullOpenTransfersForTruck(args?: {
//     truckId: string
// }): InfallibleUseResult<BundledTransfer[]> {
//     const mama = useMothership();
//     const xfersTqrv = useFullOpenTransfersForTruck(args);
//     const bundlesTqrv = useQueries({
//         queries: xfersTqrv.data.map(xfer => watchBundleByIdTqo(mama, xfer && {bundleId: xfer.bundle.id})),
//         combine: mergeUseQueriesWithInfill,
//     });
//     const bundlesById = useMemo(() =>
//             Object.fromEntries(cleanse(bundlesTqrv.data).map(bundle => ([bundle.id, bundle])))
//         , [bundlesTqrv.data]);
//
//     if (xfersTqrv.isPending || bundlesTqrv.isPending) {
//         return pendingUseResult;
//     }
//     if (xfersTqrv.isError) {
//         throw errorize(xfersTqrv.errors);
//     }
//     if (bundlesTqrv.isError) {
//         throw errorize(bundlesTqrv.errors);
//     }
//     const rv = cleanse(xfersTqrv.data).map(raw => {
//         const bundle = bundlesById[raw.bundle.id];
//         if (!bundle) {
//             throw new Error(`no such bundle ${raw.bundle.id}`);
//         }
//         return BundledTransfer.parse({...raw, bundle});
//     });
//     return successUseResult(rv);
// }

// export interface BundleExpandedTransfer {
//     transfer: Transfer;
//     bundle: Bundle;
// }

// export function ensureBundleExpandedTransfers({tqc, mama, transferStubs}: {
//     tqc: QueryClient,
//     mama: KingBasura,
//     transferStubs: TransferStub[]
// }) {
//     return Promise.all(transferStubs.map(stub =>
//         ensureTransferById({tqc, mama, args: {transferId: stub.id}})
//             .then(tqrv => ensureBundleById({tqc, mama, args: {bundleId: tqrv.transfer.bundleId}})
//                 .then(bundle => {
//                     return {transfer: tqrv.transfer, bundle};
//                 }))
//     ));
// }

// there's no point in enabling these explicitly. just pass an empty array and
// you will get an empty array back
// export interface UseBundleExpandedTransfersRequest {
//     transfers: Transfer[],
// }
//
// export function useBundleExpandedTransfers(args ?: UseBundleExpandedTransfersRequest) {
//     const mama = useMothership();
//
//     return useQueries({
//         queries: (args?.transfers ?? []).map(transfer => queryOptions({
//             queryKey: bundleByIdTqk({bundleId: transfer.bundleId}),
//             queryFn: () => mama.watchBundleById({bundleId: transfer.bundleId})
//                 .then(({bundle}) => ({transfer, bundle})),
//         })),
//
//         combine: mergeUseQueriesWithInfill,
//     });
// }

export const OffloadableBundleIdsRequest = z.object({
    truckId: z.string(),
    venueId: z.string(),
});
export type OffloadableBundleIdsRequest = z.infer<typeof OffloadableBundleIdsRequest>;

export function useOffloadableBundleIds(args?: OffloadableBundleIdsRequest): InfallibleUseResult<string[]> {
    const mama = useMothership();
    const truckId = args?.truckId;
    const loadedTqrv
        = useFullOpenTransfersForTruck(truckId ? {truckId} : undefined);
    const loadedTransfers = loadedTqrv.data ? cleanse(loadedTqrv.data) : NADA;
    const loadedBundleIds = loadedTransfers.map(xfer => xfer.bundle.id);
    const loadedBundleTqrv = useQueries(loadedTqrv.isSuccess
        ? {
            queries: loadedBundleIds.map(bundleId => watchBundleByIdTqo(mama, {bundleId})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const rv = useMemo(() => {
        if (!args || loadedTqrv.isPending || loadedBundleTqrv.isPending) {
            return pendingUseResult;
        }

        const loadedBundles = loadedBundleTqrv.data;
        const rv: string[] = [];
        for (const bundle of loadedBundles) {
            if (bundle?.destination?.id === args.venueId) {
                rv.push(bundle.id);
            }
        }

        return successUseResult(rv);
    }, [args, loadedBundleTqrv.data, loadedBundleTqrv.isPending, loadedTqrv.isPending]);

    return rv;
}

export function useAwaitingScheduledActions(args ?: WatchScheduledTransfersAwaitingTruckRequest) {
    return useFullScheduledTransfersAwaitingTruck(args);
    // const mama = useMothership();
    // const {data: stubsData} = useRefreshableQuery(queryOptions({ee
    //     queryKey: scheduledTransfersAwaitingTruckTqk({truckId, venueId}),
    //     queryFn: () => mama.watchScheduledTransfersAwaitingTruck({truckId, venueId}),
    //     ...rest,
    // }));
    // const stubs = stubsData?.scheduledTransfers;
    // const {data: xfers, isSuccess: isTransfersSuccess} = useQueries({
    //     queries: (stubs ?? []).map(stub => queryOptions({
    //         queryKey: scheduledTransferByIdTqk({scheduledTransferId: stub.id}),
    //         queryFn: () => mama.watchScheduledTransferById({scheduledTransferId: stub.id}),
    //         ...rest
    //     })),
    //     combine: useMappingMergeUseQueryResultCombiner(
    //         (rpc) => rpc.scheduledTransfer)
    // });
    //
    // const rv = useMemo(() => {
    //     if (!!stubs && isTransfersSuccess) {
    //         return {data: xfers, isSuccess: true};
    //     } else {
    //         return MADA;
    //     }
    // }, [isTransfersSuccess, stubs, xfers]);
    //
    // return rv;
}

export function useStartBundleOnloadingMutation() {
    const mama = useMothership();
    return useMutation<StartBundleOnloadingResponse, DefaultError, StartBundleOnloadingRequest>({
        mutationKey: [startBundleOnloadingVerb],
        mutationFn: (args) => mama.startBundleOnloading(args),
    });
}

export function useCompleteBundleOnloadingMutation() {
    const mama = useMothership();
    return useMutation<CompleteBundleOnloadingResponse, DefaultError, CompleteBundleOnloadingRequest>({
        mutationKey: [completeBundleOnloadingVerb],
        mutationFn: (args) => mama.completeBundleOnloading(args),
    });
}

export function useStartBundleOffloadingMutation() {
    const mama = useMothership();
    return useMutation<StartBundleOffloadingResponse, DefaultError, StartBundleOffloadingRequest>({
        mutationKey: [startBundleOffloadingVerb],
        mutationFn: (args) => mama.startBundleOffloading(args),
    });
}

export function useCompleteBundleOffloadingMutation() {
    const mama = useMothership();
    return useMutation<CompleteBundleOffloadingResponse, DefaultError, CompleteBundleOffloadingRequest>({
        mutationKey: [completeBundleOffloadingVerb],
        mutationFn: (args) => mama.completeBundleOffloading(args),
    });
}

export function useSpawnGroundedBundleMutation() {
    const mama = useMothership();
    return useMutation<SpawnGroundedBundleResponse, DefaultError, NewGroundedBundleFood>({
        mutationKey: [spawnGroundedBundleVerb],
        mutationFn: (food) => mama.spawnGroundedBundle({food}),
    });
}

export function useStampBundleMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useMutation<StampBundleResponse, DefaultError, NewBundleStampFood>({
        mutationKey: [stampBundleVerb],
        mutationFn: (food) => mama.stampBundle({food}),
        onSuccess: ({stamp: {subjectId}}) => tqc.invalidateQueries(
            {queryKey: stampsForBundleTqk({bundleId: subjectId})}),
    });
}

export function useAttachPhotoToStampMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const bomb = useBomb();
    return useMutation<AttachPhotoToStampResponse, DefaultError, AttachPhotoToStampRequest & {
        subjectId: string
    }>({
        mutationKey: [attachPhotoToStampVerb],
        mutationFn: (food) => mama.attachPhotoToStamp(food),
        onSuccess: (_, food) => {
            // TODO other kinds
            if (food.subjectClass === StampableClassname.enum.Bundle) {
                Promise.all([
                    tqc.invalidateQueries({queryKey: bundleStampByIdTqk({stampId: food.stampId})}),
                    tqc.invalidateQueries({queryKey: stampsForBundleTqk({bundleId: food.subjectId})}),
                ]).catch(bomb);
            }
        },
    });
}

export function usePatchBundleMutation() {
    const mama = useMothership();
    return useMutation<PatchBundleResponse, DefaultError, PatchBundleRequest>({
        mutationKey: [patchBundleVerb],
        mutationFn: (args) => mama.patchBundle(args),
    });
}

export function useDisclaimScheduledTransferMutation() {
    const mama = useMothership();
    return useMutation<DisclaimScheduledTransferResponse, DefaultError, DisclaimScheduledTransferRequest>({
        mutationKey: [disclaimScheduledTransferVerb],
        mutationFn: (args) =>
            mama.disclaimScheduledTransfer(
                {transferId: args.transferId, removeTruck: args.removeTruck}),
    });
}

export function useMountTruckMutation() {
    const mama = useMothership();
    return useMutation<MountTruckResponse, DefaultError, MountTruckRequest>({
        mutationKey: [mountTruckVerb],
        mutationFn: (args) => mama.mountTruck(args),
        onSuccess: (_, variables) => {
            const truckId = variables.truckId;
            return Promise.all([
                mama.watchLastTriplegForTruck({truckId}),
                mama.watchOpenTriplegForTruck({truckId}),
            ]);
        }
    });
};

export function useDismountTruckMutation() {
    const mama = useMothership();
    return useMutation<DismountTruckResponse>({
        mutationKey: [dismountTruckVerb],
        mutationFn: () => mama.dismountTruck(),
    });
}

export function usePatchScheduledTransferMutation() {
    const mama = useMothership();
    return useMutation<PatchScheduledTransferResponse, DefaultError, PatchScheduledTransferRequest>({
        mutationKey: [patchScheduledTransferVerb],
        mutationFn: (args) => mama.patchScheduledTransfer(args),
    });
}

export function usePatchTriplegMutation() {
    const mama = useMothership();
    return useMutation<PatchTriplegResponse, DefaultError, PatchTriplegRequest>({
        mutationKey: [patchTriplegVerb],
        mutationFn: (args) => mama.patchTripleg(args),
    });
}

// export type FieldCancelRequest = {
//     bundle: Bundle; enabled?: boolean;
// } | {
//     bundle?: Bundle; enabled: false;
// };

// export function useSpawnBundleSurchargeMutation() {
//     const mama = useMothership();
//     return useMutation<SpawnBundleSurchargeResponse, DefaultError, SpawnBundleSurchargeRequest>({
//         mutationKey: [spawnBundleSurchargeVerb],
//         mutationFn: (args) => mama.spawnBundleSurcharge(args),
//     });
// }
//
// export function useRetireBundleSurchargeMutation() {
//     const mama = useMothership();
//     return useMutation<
//         { bundleId: string },
//         DefaultError,
//         { surcharge: BundleSurcharge }>({
//         mutationKey: [retireBundleSurchargeVerb],
//         mutationFn: ({surcharge}) => (surcharge.id
//             ? mama.retireBundleSurcharge({surchargeId: surcharge.id})
//             : Promise.resolve())
//             .then(() => ({bundleId: surcharge.bundleId})),
//     });
// }

// export function usePatchBundleSurchargeMutation() {
//     const mama = useMothership();
//     return useMutation<{ bundleId: string },
//         DefaultError,
//         { existing: BundleSurcharge, patch: BundleSurchargePatch }>({
//         mutationKey: [patchBundleSurchargeVerb],
//         mutationFn: ({existing, patch}) =>
//             mama.patchBundleSurcharge({surchargeId: existing.id, patch})
//                 .then(() => ({bundleId: existing.bundleId})),
//     });
// }

export function useSchedulePickupMutation() {
    const mama = useMothership();
    return useMutation<ScheduleBundlePickupResponse, DefaultError, ScheduleBundlePickupRequest>({
        mutationKey: [scheduleBundlePickupVerb],
        mutationFn: (args) => mama.scheduleBundlePickup(args),
    });
}

// export function arrivalRefreshSet({truckId}: { truckId: string }) {
//     return new RefreshSetManager([
// //        {queryKey: triplegByIdTqk({id: truckId})},
// //        {queryKey: lastTriplegForTruckTqk({truckId})},
//         {queryKey: [triplegsForTruckDuringVerb, {truckId}]},
//         {queryKey: openTransfersForTruckTqk({truckId})},
//     ]);
// }

export function useArriveMutation() {
    const mama = useMothership();
    return useMutation<ArriveResponse, DefaultError, { tripleg: Tripleg }>({
        mutationKey: [arriveVerb],
        mutationFn: ({tripleg}) => mama.arrive({triplegId: tripleg.id}),
    });
}

export function useStartTriplegMutation() {
    const mama = useMothership();
    return useMutation<StartTriplegResponse, DefaultError, NewTriplegFood>({
        mutationKey: [startTriplegVerb],
        mutationFn: (food) => mama.startTripleg({food})
    });
}

export function useStampWasteMutation() {
    const mama = useMothership();
    return useMutation<StampWasteResponse, DefaultError, StampWasteRequest>({
        mutationKey: [stampWasteVerb],
        mutationFn: (args) => mama.stampWaste(args)
    });
}

export interface UpsertWasteOrSurchargeFlavorResult {
    result: WasteFlavorUpsertResult | SurchargeFlavorUpsertResult;
    isSurcharge: boolean;
}

export function useUpsertWasteFlavorMutationOpts() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const {data: units} = useUnitArmap();
    const mutf = useCallback((args: UpsertWasteFlavorRequest) => {
        if (!units) {
            return Promise.reject(new Error("no units"));
        }

        const food = args.food;

        let asSurcharge: NewSurchargeFlavorFood | undefined;

        // 2026.01.19 this used to consider *non*-birthable things not to be surcharges,
        // but i fear this is backwards. birthable is a shinwa legacy thing.
        if (((food.showToDriver ?? food.showToIntaker) ?? food.showToInspector)
            || !!food.birthable) {
            // these are not surcharges
        } else {
            if (!food.invoiceUnitId) {
                return Promise.reject(new Error("surcharge uses invoice unit"));
            }

            const invu = units.byId.get(food.invoiceUnitId);
            if (!invu) {
                return Promise.reject(new Error(`no such unit id ${food.invoiceUnitId}`));
            }

            const niucode = Number.parseInt(invu.code);
            const scucode = (50 + niucode).toString()
            const scu = units.byCode.get(scucode);
            if (!scu) {
                return Promise.reject(new Error(`no such unit code ${scucode}`));
            }
            const scuid = scu.id;

            asSurcharge = {
                code: food.code,
                displayName: food.displayName,
                sortName: food.sortName,
                abbrev: food.abbrev,
                unitId: scuid,
            };
        }

        const performUpsert$ = asSurcharge
            ? mama.upsertSurchargeFlavor({food: asSurcharge})
            : mama.upsertWasteFlavor(args);
        return performUpsert$.then(({result}) =>
            ({result, isSurcharge: !!asSurcharge}))
    }, [mama, units]);

    return {
        mutationKey: [upsertWasteFlavorVerb],
        mutationFn: mutf,
        onSuccess: ({result, isSurcharge}: UpsertWasteOrSurchargeFlavorResult) => {
            const flavorId = result.newId ?? result.existingId;
            if (flavorId) {
                if (isSurcharge) {
                    return tqc.invalidateQueries({queryKey: surchargeFlavorByIdTqk({surchargeFlavorId: flavorId})});
                } else {
                    return Promise.all([
                        tqc.invalidateQueries({queryKey: wasteFlavorRevisionCountTqk({wasteFlavorId: flavorId})}),
                        tqc.invalidateQueries({queryKey: wasteFlavorByIdTqk({wasteFlavorId: flavorId})}),
                    ]);
                }
            }
        },
    };
}

export function useUpsertWasteFlavorMutation() {
    const tqmo = useUpsertWasteFlavorMutationOpts();
    return useMutation(tqmo);
}

export function useActiveDaughtersOfWaste(args ?: AllDaughtersOfWasteRequest): InfallibleUseResult<Waste[]> {
    const allDaughtersTqrv = useFullAllDaughtersOfWaste(args);
    const rv = useMemo<InfallibleUseResult<Waste[]>>(() => {
        if (allDaughtersTqrv.isPending) {
            return pendingUseResult;
        }

        // if (allDaughtersTqrv.isError) {
        //     throw errorize(allDaughtersTqrv.errors);
        // }

        const activeDaughters = allDaughtersTqrv.data.filter(
            (maybe): maybe is Waste => !!maybe && isActive(maybe));

        return successUseResult(activeDaughters);
    }, [allDaughtersTqrv.data, allDaughtersTqrv.isPending]);

    return rv;
}

export function useSpawnWasteMutation() {
    const mama = useMothership();
    return useMutation<SpawnWasteResponse, DefaultError, {
        spawnFood: NewWasteFood,
        bundleId: string
    }>({
        mutationKey: [spawnWasteTqk],
        mutationFn: ({spawnFood, bundleId}) =>
            mama.spawnWaste({food: spawnFood})
                .then(({waste}) => {
                    if (!waste.id) {
                        throw new Error("waste spawn failed");
                    }
                    return mama.attachWasteToBundle({
                        bundleId: bundleId,
                        wasteId: waste.id,
                    }).then(() => {
                        return {waste};
                    });
                }),
    });
}

export function usePatchWasteMutation() {
    const mama = useMothership();
    const tqc = useQueryClient();
    return useMutation<PatchWasteResponse, DefaultError, PatchWasteRequest>({
        mutationKey: [patchWasteVerb],
        mutationFn: (args) => mama.patchWaste(args),
        onSuccess: ({waste}) => {
            const iset: QueryFilters[] = [
                {queryKey: wasteByIdTqk({wasteId: waste.id})},
                {queryKey: allWasteRevisionStubsTqxk({wasteId: waste.id})},
            ];
            const maybeBundle
                = tqc.getQueryData<WatchBundleForWasteResponse>(bundleForWasteTqk({wasteId: waste.id}));
            if (maybeBundle?.bundle) {
                iset.push({queryKey: wastesForBundleTqk({bundleId: maybeBundle.bundle.id})});
            }
            const mothersData = tqc.getQueryData<MothersOfWasteResponse>(mothersOfWasteTqk({wasteId: waste.id}));
            if (mothersData?.mothers) {
                for (const mom of mothersData.mothers) {
                    iset.push({queryKey: allDaughtersOfWasteTqk({wasteId: mom.id})});
                }
            }
            return Promise.all(iset.map(qf => tqc.invalidateQueries(qf)));
        },
    });
}

export function useRetireWasteMutation() {
    const mama = useMothership();
    return useMutation<RetireWasteResponse, DefaultError, RetireWasteRequest>({
        mutationKey: [retireWasteVerb],
        mutationFn: (args) => mama.retireWaste(args),
    });
}

export function allWasteRevisionStubsTqxk({wasteId}: { wasteId: string }) {
    return [allWasteRevisionStubsVerb, {wasteId}];
}

export function allWasteRevisionStubsTqxo(mama: KingBasura, {wasteId}: { wasteId: string }) {
    return queryOptions({
        queryKey: allWasteRevisionStubsTqxk({wasteId}),
        queryFn: () => mama.allWasteRevisionStubs({wasteId})
            .then((wireds) => wireds.revisions.map(
                wired => {
                    const rv: RevisionStub = {
                        author: wired.author,
                        validSpan: spawnTstzSpan(wired.validSpan),
                    };
                    return rv;
                })),
    });
}

export function useLiveLastValidWasteById(args ?: { wasteId: string }) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery({
        queryKey: wasteByIdTqk(args),
        queryFn: args
            ? () => mama.watchWasteById(args).then(rpc => rpc.waste
                ? rpc as { waste: Waste }
                : mama.lastValidWasteById(args))
            : skipToken,
        select: (rpc) => rpc.waste,
    });
}

export function useAllWasteRevisionStubs(args: { wasteId: string }) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery(allWasteRevisionStubsTqxo(mama, args));
}

export function useStoreWastePermanentMutation() {
    const mama = useMothership();
    return useMutation<unknown, DefaultError, StoreWastePermanentRequest>({
        mutationKey: [storeWastePermanentVerb],
        mutationFn: (args) => mama.storeWastePermanent(args),
    });
}

// export interface FinalInspectWasteRequest {
//     wasteId: string;
// }

export function useAdmitMutation() {
    const mama = useMothership();
    return useMutation({
        mutationKey: [admitVerb],
        mutationFn: (args: AdmitRequest) => mama.admit(args),
    });
}

export function makeTransferFleshingQueryFn(tqc: QueryClient, mama: KingBasura) {
    return (stubs: TransferStub[]) => Promise.all(stubs.map(
        stub => tqc.ensureQueryData({
            ...watchTransferByIdTqo(mama, {transferId: stub.id}),
        })));
}

export function wasteLocationTqo(mama: KingBasura, args?: WatchWasteLocationRequest) {
    return queryOptions({
        queryKey: wasteLocationTqk(args),
        queryFn: args ? () => mama.watchWasteLocation(args) : skipToken,
        select: rpc => rpc.curloc,
    });
}

export function useWasteLocation(args?: WatchWasteLocationRequest): InfallibleUseQueryResult<TruckOrVenueStub, Error> {
    const mama = useMothership();
    return useRefreshableInfallibleQuery(wasteLocationTqo(mama, args));
}

export function useSetSystemConfigMutation() {
    const mama = useMothership();
    return useMutation<unknown, DefaultError, SystemConfig>({
        mutationKey: [setSystemConfigVerb],
        mutationFn: (cfg: SystemConfig) => mama.setSystemConfig({sysconf: cfg}),
    });
}

export function usePatchSurchargeMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useMutation<PatchSurchargeResponse, DefaultError, PatchSurchargeRequest>({
        mutationKey: [patchSurchargeVerb],
        mutationFn: (args: PatchSurchargeRequest) => mama.patchSurcharge(args),
        onSuccess: ({surcharge}) => {
            const iset: QueryFilters[] = [
                {queryKey: surchargeByIdTqk({subjectClass: surcharge.subjectClass, surchargeId: surcharge.id})},
                {queryKey: allSurchargeRevisionsTqk({subjectClass: surcharge.subjectClass, surchargeId: surcharge.id})},
                {
                    queryKey: surchargesForSubjectTqk({
                        subjectClass: surcharge.subjectClass,
                        subjectId: surcharge.subjectId
                    })
                }
            ];
            return Promise.all(iset.map(qf => tqc.invalidateQueries(qf)));
        },
    });
}

export function useLiveLastValidSurchargeById(args ?: { subjectClass: SurchargeableClassname, surchargeId: string }) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery({
        queryKey: surchargeByIdTqk(args),
        queryFn: args
            ? () => mama.watchSurchargeById(args).then(rpc => rpc.surcharge
                ? rpc as { surcharge: Surcharge }
                : mama.lastValidSurchargeById(args))
            : skipToken,
        select: (rpc) => rpc.surcharge,
    });
}

// export function useAllJobSurchargeRevisions(args: { wasteId: string }) {
//     const mama = useMothership();
//     return useRefreshableInfallibleQuery(allJobSurchargeRevisionsTqo(mama, args));
// }

export function useZrpcVersionInfo(): InfallibleUseResult<VersionInfo> {
    return useGetVersionInfo();
}

export interface Coded {
    code: string;
}

export type CodedArmap<T extends Ided & Coded> = Armap<T> & {
    byCode: Map<string, T>;
}

export function useUnitArmap(): InfallibleUseResult<CodedArmap<Unit>> {
    const mama = useMothership();
    return useInfallibleQuery({
        ...watchUnitsTqo(mama),
        select: (tqrv) => {
            const rv = {
                ...armapify(tqrv.units),
                byCode: new Map<string, Unit>(tqrv.units.map(unit => ([unit.code, unit])))
            } as const;
            return rv;
        },
    });
}

export function usePackingModeArmap(): InfallibleUseResult<CodedArmap<PackingMode>> {
    const mama = useMothership();
    return useInfallibleQuery({
        ...watchPackingModesTqo(mama),
        select: (tqrv) => {
            const rv = {
                ...armapify(tqrv.modes),
                byCode: new Map<string, PackingMode>(tqrv.modes.map(mode => ([mode.code, mode]))),
            } as const;
            return rv;
        },
    });
}

export function useManifestTypeArmap(): InfallibleUseResult<Armap<ManifestType>> {
    const mama = useMothership();
    return useInfallibleQuery({
        ...watchManifestTypesTqo(mama),
        select: (tqrv) => armapify(tqrv.maniTypes),
    });
}


